import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { authGuard } from '@app/core/guards/auth.guard';

import { MainLayoutComponent } from "@app/core/layout/app-layouts/main-layout.component";
import { AuthLayoutComponent } from "@app/core/layout/app-layouts/auth-layout.component";
import { DashboardComponent } from "./cp/dashboard/dashboard.component";
import { DispatchHistorySearchComponent } from "./cp/dispatch-history/dispatch-history-search.component";
import { EmployeeComponent } from "./cp/employees/employee.component";
import { EmpTimeoffComponent } from "./cp/employees/emp-timeoff/emp-timeoff.component";
import { ClockInOutComponent } from "./cp/employees/clock-in-out/clock-in-out.component";
import { PurchaseOrdersComponent } from "./cp/inventory/purchase-orders/purchase-orders.component";
import { PartsComponent } from "./cp/inventory/parts/parts.component";
import { CycleCountingComponent } from "./cp/inventory/cycle-counting/cycle-counting.component";
import { WarehouseTransferComponent } from "./cp/inventory/warehouse-transfer/warehouse-transfer.component";
import { InventoryThresholdsComponent } from "./cp/inventory/inventory-thresholds/inventory-thresholds.component";
import { StockPickupComponent } from "./cp/inventory/stock-pickup/stock-pickup.component";
import { ConstructionJobInformationComponent } from "./cp/construction/Job-information/job-information.component";
import { DQOIMainComponent } from "./cp/dqoi/dqoi-main.component";
import { ConstructionContractInformationComponent } from "./cp/construction/contract-information/contract-information.component";
import { PmBoardComponent } from "./cp/construction/pm-board/pm-board.component";
import { ConstructionWIPContractBoardComponent } from "./cp/construction/wip-contract-board/wip-contract-board.component";
import { OpenPOComponent } from "./cp/inventory/open-po/open-po.component";
import { VendorReturnsComponent } from "./cp/inventory/vendor-returns/vendor-returns.component";
import { DispatchBoardComponent } from './cp/dispatch-board/dispatch-board.component';
import { WeekBoardComponent } from './cp/boards/week-board/week-board.component';
import { TimeOffCalComponent } from './cp/boards/time-off-cal/time-off-cal.component';
import { ConstructionMainComponent } from "./cp/construction/construction-main/construction-main.component";
import { ContractHistorySearchComponent } from "./cp/construction/contract-history/contract-history-search.component";
import { RefTrackAdminComponent } from "./cp/refTracking/admin/reftrack-admin.component";
import { CommissionMaintenanceComponent } from "./cp/payroll/commission-maintenance/commission-maintenance.component";
import { BulkPaymentsComponent } from './cp/accounting/bulk-payments/bulk-payments.component';
import { ChartOfAccountsComponent } from './cp/accounting/chart-of-accounts/chart-of-accounts.component';
import { AccountClassesComponent } from './cp/accounting/account-classes/account-classes.component';
import { PriceChangeBenefitComponent } from './cp/accounting/price-change-benefit/price-change-benefit.component';
import { EventManagerComponent } from './cp/system-config/event-manager/event-manager.component';
import { MobilePreviewComponent } from "./cp/refTracking/admin/mobile-preview/mobile-preview.component";
import { MobileLandingComponent } from "./cp/system-config/mobile-landing/mobile-landing.component";
import { MobileReleaseVersionsComponent } from './cp/system-config/mobile-release-versions/mobile-release-versions.component';
import { SeattleComponent } from "./cp/system-config/mobile-landing/seattle/seattle.component";

import { CanDeactivateGuard } from './cp/shared/guards/deactivate.guard';
import { QuickViewComponent } from "./cp/quick-view/quick-view.component";
import { CustomDownloadsComponent } from "./cp/shared/components/custom-downloads/custom-downloads.component";
import { DispatchBoardCalendarComponent } from "./cp/dispatch-board-calendar/dispatch-board-calendar.component";
import { WipReportComponent } from "./cp/construction/wip-report/wip-report.component";
import { BaseDataComponent } from "./cp/domain/basedata/basedata.component";
import { OrderSearchComponent } from "./cp/order-search/order-search.component";
import { VendorCreditsComponent } from "./cp/orders/modal/vendor-credits/vendor-credits.component";
import { DispatchBoardCalendarAllComponent } from "./cp/dispatch-board-calendar/dispatch-board-all-calendar/dispatch-board-all-calendar.component";
import { ReportsComponent } from "@app/cp/reports/reports.component";
import { ContractWipBoardComponent } from "./cp/construction/contract-wip-board/contract-wip-board.component";
import { UserSettingDialogComponent } from "./cp/employees/user-setting/user-setting.component";
import { OpenPOSubComponent } from "./cp/inventory/open-po-sub/open-po-sub.component";
import { PartsHistorySearchComponent } from "./cp/inventory/parts-history/parts-history-search.component";
import { PartsMergeSearchComponent } from "./cp/inventory/parts-merge/parts-merge-search.component";
import { EquipmentBoardComponent } from "./cp/construction/equipment-board/equipment-board.component";
import { PartsCostHistoryComponent } from "./cp/inventory/parts-cost-history/parts-cost-history.component";
import { InvoiceActionComponent } from "./cp/invoices/invoice-action/invoice-action.component";
import { StockReplenishmentComponent } from "./cp/inventory/parts/stock-replenishment/stock-replenishment.component";
import { PotentialCustomersComponent } from "./cp/construction/potential-customers/potential-customers.component";
import { FeatureFlagResolver } from "@app/cp/shared/resolvers/feature-flag.resolver";
import { ZebraPrinterConfigComponent } from "./cp/configuration/zebra-printer-config/zebra-printer-config.component";
import { CycleCountingHistoryComponent } from "./cp/inventory/cycle-counting-history/cycle-counting-history.component";
import { BrowserUtils } from "@azure/msal-browser";
import { MsalGuard } from "@azure/msal-angular";
import { UserLoadedResolver } from "./cp/shared/resolvers/user-loaded.resolver";
import { OpenPOBillsComponent } from "./cp/inventory/open-po-bills/open-po-bills.component";
import { OpenPOVendorCreditsComponent } from "./cp/inventory/open-po-vendor-credits/open-po-vendor-credits.component";

const childResolvers = {
    featureFlagsLoaded: FeatureFlagResolver,
    user: UserLoadedResolver
};

const routes: Routes = [
    {
        path: "auth",
        component: AuthLayoutComponent,
        loadChildren: () => import('./core/auth/auth.routes').then(m => m.AUTH_ROUTES)
    },
    {
        path: "dispatchimages",
        loadChildren: () => import('./cp/external-images/external-images.module').then(m => m.ExternalImagesModule),
        data: { pageTitle: "Dispatch Images" }
    },
    {
        path: "login",
        redirectTo: "auth/login",
    },
    {
        path: "mobile",
        component: MobileLandingComponent
    },
    {
        path: "mobile/seattle",
        component: SeattleComponent
    },
    {
        path: "authenticate/:userId/:sessionId",
        redirectTo: "auth/authenticate/:userId/:sessionId"
    },
    {
        path: "authenticate/:userId/:sessionId/:redirect",
        redirectTo: "auth/authenticate/:userId/:sessionId/:redirect"
    },
    {
        path: "miscellaneous",
        loadComponent: () => import('./core/error-pages/error-layout.component').then(m => m.ErrorLayoutComponent),
        loadChildren:
            () => import('./core/error-pages/error-pages.routes').then(m => m.ERROR_PAGES_ROUTES),
        data: { pageTitle: "Miscellaneous" },
    },

    {
        path: "",
        canActivateChild: [MsalGuard],
        component: MainLayoutComponent,
        children: [
            {
                path: "",
                redirectTo: "dashboard",
                pathMatch: "full"
            },
            {
                path: "dashboard",
                component: DashboardComponent,
                data: { pageTitle: "Dashboard" },
                resolve: childResolvers
            },
            {
                path: "dispatch/search",
                component: DQOIMainComponent,
                data: { pageTitle: "Dispatch Search", saveLastSearchToQueryParams: true },
                resolve: childResolvers
            },
            {
                path: "quote/search",
                component: DQOIMainComponent,
                data: { pageTitle: "Quote Search", saveLastSearchToQueryParams: true },
                resolve: childResolvers
            },
            {
                path: "order/search",
                component: DQOIMainComponent,
                data: { pageTitle: "Order Search", saveLastSearchToQueryParams: true },
                resolve: childResolvers
            },
            {
                path: "invoice/search",
                component: DQOIMainComponent,
                data: { pageTitle: "Invoice Search", saveLastSearchToQueryParams: true },
                resolve: childResolvers
            },
            {
                path: "dispatch/history/:customerId/:locationId",
                component: DispatchHistorySearchComponent,
                data: { pageTitle: "Dispatch History" },
                resolve: childResolvers
            },
            {
                path: "dispatch/history/:customerId",
                component: DispatchHistorySearchComponent,
                data: { pageTitle: "Dispatch History" },
                resolve: childResolvers
            },
            {
                path: "dispatch/history",
                component: DispatchHistorySearchComponent,
                data: { pageTitle: "Dispatch History" },
                resolve: childResolvers
            },
            {
                path: "dispatch/invoiceAction",
                component: InvoiceActionComponent,
                data: { pageTitle: "Invoice History" },
                resolve: childResolvers
            },
            {
                path: "boards/dispatchboard",
                component: DispatchBoardComponent,
                data: { pageTitle: "Dispatch Board" },
                resolve: childResolvers
            },
            {
                path: "boards/dispatchboardcalendar",
                component: DispatchBoardCalendarComponent,
                data: { pageTitle: "Dispatch Board Calendar" },
                resolve: childResolvers
            },
            {
                path: "boards/weekboard",
                component: WeekBoardComponent,
                data: { pageTitle: "Week Board" },
                resolve: childResolvers
            },
            {
                path: "boards/timeoffcalendar",
                component: TimeOffCalComponent,
                data: { pageTitle: "Time Off Calendar" },
                resolve: childResolvers
            },
            {
                path: "construction/invoice",
                component: ConstructionMainComponent,
                data: { pageTitle: "Construction Invoices" },
                resolve: childResolvers
            },
            {
                path: "construction/order",
                component: ConstructionMainComponent,
                data: { pageTitle: "Construction Orders" },
                resolve: childResolvers
            },
            {
                path: "construction/quote",
                component: ConstructionMainComponent,
                data: { pageTitle: "Construction Quotes" },
                resolve: childResolvers
            },
            {
                path: "construction/jobs",
                component: ConstructionMainComponent,
                data: { pageTitle: "Construction Jobs" },
                resolve: childResolvers
            },
            {
                path: "construction/job-information",
                component: ConstructionJobInformationComponent,
                data: { pageTitle: "Construction Job Information" },
                resolve: childResolvers
            },
            {
                path: "construction/contracts",
                component: ConstructionMainComponent,
                data: { pageTitle: "Construction Contracts" },
                canDeactivate: [CanDeactivateGuard],
                resolve: childResolvers
            },
            {
                path: "construction/opencontract",
                component: ConstructionContractInformationComponent,
                data: { pageTitle: "Construction Open Contracts" },
                resolve: childResolvers
            },
            {
                path: "construction/pmboard",
                component: PmBoardComponent,
                data: { pageTitle: "PM Board" },
                resolve: childResolvers
            },
            {
                path: "construction/cp-potential-customers",
                component: PotentialCustomersComponent,
                data: { pageTitle: "Potential Customer" },
                resolve: childResolvers
            },
            {
                path: "construction/wipreport",
                component: WipReportComponent,
                data: { pageTitle: "WIP Report" },
                resolve: childResolvers
            },
            {
                path: "construction/wipcontractboard",
                component: ConstructionWIPContractBoardComponent,
                data: { pageTitle: "Construction WIP Board" },
                resolve: childResolvers
            },
            {
                path: "construction/ContractWipboard",
                component: ContractWipBoardComponent,
                data: { pageTitle: "Contract WIP Board" },
                resolve: childResolvers
            },
            {
                path: "construction/EquipmentBoard",
                component: EquipmentBoardComponent,
                data: { pageTitle: "Equipment Job Board" },
                resolve: childResolvers
            },
            {
                path: "construction/contracthistory/:customerId/:locationId",
                component: ContractHistorySearchComponent,
                data: { pageTitle: "Construction Contract History" },
                resolve: childResolvers
            },
            {
                path: "construction/contracthistory/:customerId",
                component: ContractHistorySearchComponent,
                data: { pageTitle: "Construction Contract History" },
                resolve: childResolvers
            },
            {
                path: "construction/contracthistory",
                component: ContractHistorySearchComponent,
                data: { pageTitle: "Construction Contract History" },
                resolve: childResolvers
            },
            {
                path: "construction",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "BidSheet" },
                resolve: childResolvers
            },
            {
                path: "construction",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "BidSheet Template" },
                resolve: childResolvers
            },
            {
                path: "construction",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "Labor Rates" },
                resolve: childResolvers
            },
            {
                path: "construction/bidsheetComp",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "Components" },
                resolve: childResolvers
            },
            {
                path: "construction/refrigerant",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "Refrigerant" },
                resolve: childResolvers
            },
            {
                path: "construction/subsistence",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "Subsistence" },
                resolve: childResolvers
            },
            {
                path: "construction/pipesadmin",
                loadChildren: () => import('./cp/construction/bidsheet/bidsheet.module').then(m => m.BidsheetModule),
                data: { pageTitle: "Pipes Admin" },
                resolve: childResolvers
            },
            {
                path: "quickview/:busType/:targetType/:targetId",
                component: QuickViewComponent,
                data: { pageTitle: "Quick View" },
                resolve: childResolvers
            },
            {
                path: "popout/:busType/:targetType/:targetId",
                component: QuickViewComponent,
                data: { pageTitle: "Quick View" },
                resolve: childResolvers
            },
            {
                path: "customers",
                loadChildren: () => import('./cp/customers/customers.module').then(m => m.CustomersModule),
                data: { pageTitle: "Customers" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/purchaseorders",
                component: PurchaseOrdersComponent,
                data: { pageTitle: "Purchase Orders" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/ordersearch",
                component: OrderSearchComponent,
                data: { pageTitle: "Order Search" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/vendorreturns",
                component: VendorReturnsComponent,
                data: { pageTitle: "Vendor Returns" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/vendorcredits",
                component: VendorCreditsComponent,
                data: { pageTitle: "Vendor Credits" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/Parts",
                component: PartsComponent,
                data: { pageTitle: "Parts" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/Parts/:InventoryId",
                component: PartsComponent,
                data: { pageTitle: "Parts" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/cycleCounting",
                component: CycleCountingComponent,
                data: { pageTitle: "Cycle Counting" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/cycleCounting?countId=",
                component: CycleCountingComponent,
                data: { pageTitle: "Cycle Counting" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/StockReplenishment",
                component: StockReplenishmentComponent,
                data: { pageTitle: "Parts" },
                resolve: childResolvers
            },

            {
                path: "orderinginventory/partshistory",
                component: PartsHistorySearchComponent,
                data: { pageTitle: "Parts History" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/cyclecountinghistory",
                component: CycleCountingHistoryComponent,
                data: { pageTitle: "Cycle Counting History" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/partscosthistory",
                component: PartsCostHistoryComponent,
                data: { pageTitle: "Parts Cost History" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/partsmerge",
                component: PartsMergeSearchComponent,
                data: { pageTitle: "Parts History" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/openpoinfo",
                component: OpenPOComponent,
                data: { pageTitle: "Purchasing Information" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/openpobillsinfo",
                component: OpenPOBillsComponent,
                data: { pageTitle: "Purchasing Information" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/openpovendorcreditsinfo",
                component: OpenPOVendorCreditsComponent,
                data: { pageTitle: "Purchasing Information" },
                resolve: childResolvers
            },
           
            {
                path: "orderinginventory/openposubinfo",
                component: OpenPOSubComponent,
                data: { pageTitle: "Sub Contractor PO's" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/warehousetransfers",
                component: WarehouseTransferComponent,
                data: { pageTitle: "Warehouse Transfers" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/inventorythresholds",
                component: InventoryThresholdsComponent,
                data: { pageTitle: "Warehouse Transfers" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory/stockpickup",
                component: StockPickupComponent,
                data: { pageTitle: "Stock Pickup" },
                resolve: childResolvers
            },
            {
                path: "fleetmanagement",
                loadChildren: () => import('./cp/fleet-management/fleet-mgmt.module').then(m => m.FleetMgmtModule),
                data: { pageTitle: "Vehicle Profile" },
                resolve: childResolvers
            },
            {
                path: "humanresources/employeemaintenance",
                component: EmployeeComponent,
                data: { pageTitle: "Employee Maintenance" },
                resolve: childResolvers
            },
            {
                path: "payroll",
                loadChildren: () => import('./cp/payroll/payroll.module').then(m => m.PayrollModule),
                data: { pageTitle: "Payroll Maintenance" },
                resolve: childResolvers
            },
            {
                path: "payroll",
                loadChildren: () => import('./cp/payroll/payroll.module').then(m => m.PayrollModule)
            },
            {
                path: "payroll/commissions",
                component: CommissionMaintenanceComponent,
                data: { pageTitle: "Commissions Maintenance" },
                resolve: childResolvers
            },
            {
                path: "orderinginventory",
                loadChildren: () => import('./cp/inventory/inventory.module').then(m => m.InventoryModule),
                data: { pageTitle: "Vendor" },
                resolve: childResolvers
            },
            {
                path: "accounting/bulkpayments",
                component: BulkPaymentsComponent,
                data: { pageTitle: "Bulk Payments" },
                resolve: childResolvers
            },
            {
                path: "accounting/chartofaccounts",
                component: ChartOfAccountsComponent,
                data: { pageTitle: "Chart of Accounts" },
                resolve: childResolvers
            },
            {
                path: "accounting/accountclasses",
                component: AccountClassesComponent,
                data: { pageTitle: "Account Classes" },
                resolve: childResolvers
            },
            {
                path: "accounting/PriceChangeBenefit",
                component: PriceChangeBenefitComponent,
                data: { pageTitle: "Price Change Benefit" },
                resolve: childResolvers
            },
            {
                path: "downloads/customdownloads",
                component: CustomDownloadsComponent,
                data: { pageTitle: "Custom Downloads" },
                resolve: childResolvers
            },
            {
                path: ":report",
                component: ReportsComponent,
                data: { pageTitle: 'Report' },
                //resolve: {FeatureFlagResolver} //Disabled as it is a separate site.
            },
            {
                path: "reports/:report",
                component: ReportsComponent,
                data: { pageTitle: 'Report' },
                //resolve: {FeatureFlagResolver} //Disabled as it is a separate site.
            },
            {
                path: "mylinks/requesttimeoff",
                component: EmpTimeoffComponent,
                data: { pageTitle: "Time Off Request(s)" },
                resolve: childResolvers
            },
            {
                path: "mylinks/clockinout",
                component: ClockInOutComponent,
                data: { pageTitle: "Clock in/out" },
                resolve: childResolvers
            },
            {
                path: "mylinks/usersettings",
                component: UserSettingDialogComponent,
                data: { pageTitle: "User Settings" },
                resolve: childResolvers
            },

            {
                path: "reftracking/admin",
                component: RefTrackAdminComponent,
                data: { pageTitle: "Refrigeration Tracking Administration" },
                resolve: childResolvers
            },
            {
                path: "reftracking/admin-mobile",
                component: MobilePreviewComponent,
                data: { pageTitle: "" },
                resolve: childResolvers
            },
            {
                path: "systemConfig/event-manager",
                component: EventManagerComponent,
                data: { pageTitle: "Event Manager" },
                resolve: childResolvers
            },
            {
                path: "systemConfig/mobile",
                component: MobileReleaseVersionsComponent,
                data: { pageTitle: "Mobile Release Versions" },
                resolve: childResolvers
            },
            {
                path: "systemConfig/t1",
                component: BaseDataComponent,
                data: { pageTitle: "Domain Data" }
            },
            {
                path: "systemConfig/zebra-print-config",
                component: ZebraPrinterConfigComponent,
                data: { pageTitle: "Zebra Printer Config" },
                resolve: { FeatureFlagResolver }
            },
            {
                path: "CalendarAll",
                component: DispatchBoardCalendarAllComponent,
                data: { pageTitle: "Calendar All" },
                resolve: childResolvers
            },
        ]
    },
    { path: "**", redirectTo: "miscellaneous/error404" }

];
const isIframe = window !== window.parent && !window.opener;
@NgModule({
    imports: [RouterModule.forRoot(routes, {
        useHash: false,
        enableTracing: false,
        initialNavigation:
            !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup()
                ? "enabledNonBlocking"
                : "disabled",
    })],
    exports: [RouterModule]
})
export class AppRoutingModule { }
